import * as React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Button } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { theme } from "./theme";

import NavigationBar from "./navigation/NavigationBar";
import Footer from "./navigation/Footer";
import TermsOfService from "./navigation/TermsOfService";
import PrivacyPolicy from "./navigation/PrivacyPolicy";

// Auth
import Entry from "./pages/Entry";
import AuthCallback from "./pages/auth/AuthCallback";
import EmailConfirmed from "./pages/auth/EmailConfirmed";
import ResetPassword from "./pages/auth/ResetPassword";

// Profile setup
import UserProfileSetup from "./pages/UserProfile";
import UserRolesSetup from "./pages/UserRoles";
import Profile from "./pages/Profile";

// Employee screens
import JourneyPillars from "./pages/JourneyPillars";
import VibeQuestionsFlow from "./pages/utils/VibeQuestionSkeleton";
import WorkshopDetails from "./pages/utils/WorkshopDetails";
import MeetingRoom from "./pages/workshopflow/MeetingRoom";

// Admin panels
import ControlPanel from "./pages/ControlPanel";
import GroupDashboard from "./pages/GroupDashboard";
import ScreenManagement from "./pages/ScreenManagement";
import WorkshopManagement from "./pages/WorkshopManagement";
import LabPhotos from "./pages/LabPhotos";

// Workshop flow
import WelcomeScreen from "./pages/WelcomeScreen";
import BlackOutIntro from "./pages/workshopflow/BlackOutIntro";
import BlackoutKnowledgeQuestion from "./pages/workshopflow/BlackoutKnowledgeQuestion";
import AwareIntroScreen from "./pages/workshopflow/AwareIntroScreen";
import AwarenessIntro from "./pages/workshopflow/AwarenessIntro";
import ThreatPerceptionQuestion from "./pages/workshopflow/ThreatPerceptionQuestion";
import AwareThreatWallScreen from "./pages/workshopflow/AwareThreatWallScreen";
import AwareCrisisVideoScreen from "./pages/workshopflow/AwareCrisisVideoScreen";
import AwareStayOrLeave from "./pages/workshopflow/AwareStayOrLeave";
import AwareBaselineScoreScreen from "./pages/workshopflow/AwareBaselineScoreScreen";

import PrepareResultScreen from "./pages/workshopflow/PrepareResultScreen";
import PrepareReflectionScreen from "./pages/workshopflow/PrepareReflectionScreen";

import {
  PrepareScenarioM1,
  PrepareScenarioM2,
  PrepareScenarioM3,
  PrepareScenarioM4,
  PrepareScenarioM5,
  PrepareScenarioM6,
  PrepareScenarioM7,
  PrepareScenarioM8,
} from "./pages/workshopflow/prepareScenarios";


import OthersSaid from "./pages/workshopflow/OthersSaid";
import OthersSaidV2 from "./pages/workshopflow/OthersSaidV2";
import StayOrLeave from "./pages/workshopflow/StayOrLeave";
import StayDecision from "./pages/workshopflow/StayDecision";
import PrepareYourself from "./pages/workshopflow/PrepareYourself";
import ScenarioPowerOutage from "./pages/scenarios/ScenarioPowerOutage";
import ScenarioBurningBuilding from "./pages/scenarios/ScenarioBurningBuilding";
import ScenarioSecuringDocuments from "./pages/scenarios/ScenarioSecuringDocuments";
import ScenarioEmergency from "./pages/scenarios/ScenarioEmergency";
import ScenarioRecoveryPosition from "./pages/scenarios/ScenarioRecoveryPosition";
import ScenarioCommunicationFailure from "./pages/scenarios/ScenarioCommunicationFailure";
import ScenarioSirene from "./pages/scenarios/ScenarioSirene";
import ScenarioCrisis from "./pages/scenarios/ScenarioCrisis";
import ScenarioOrientation from "./pages/scenarios/ScenarioOrientation";
import ScenarioInformationOverload from "./pages/scenarios/ScenarioInformationOverload";
import ScenarioMentalOverwhelm from "./pages/scenarios/ScenarioMentalOverwhelm";
import ScenarioIsolation from "./pages/scenarios/ScenarioIsolation";
import ScenarioEvacuation from "./pages/scenarios/ScenarioEvacuation";
import EvacuationTaskReview from "./pages/workshopflow/EvacuationTaskReview";
import ScenarioPhysicalLimit from "./pages/scenarios/ScenarioPhysicalLimit";
import ShareStateScreen from "./pages/workshopflow/ShareStateScreen";
import FamilyPreparedness from "./pages/workshopflow/FamilyPreparedness";
import CommunityPreparedness from "./pages/workshopflow/CommunityPreparedness";
import AvailabilityAwareness from "./pages/workshopflow/AvailabilityAwareness";
import AvailabilityCheck from "./pages/workshopflow/AvailabilityCheck";
import EmployeeScreen from "./pages/EmployeeScreen";
import TakeAPhoto from "./pages/workshopflow/TakeAPhoto";
import ShareInvitation from "./pages/workshopflow/ShareInvitation";
import ShareCompletedScreen from "./pages/workshopflow/ShareCompletedScreen";

import { I18nProvider } from "./i18n/I18nProvider";
import { supabase } from "./logic/client";
import { canUseControlPanel, CompanyRole } from "./logic/cache";
import LanguageSwitcher from "./logic/LanguageSwitcher";
import { Category } from "./pages/utils/Utils";


const FALLBACK_VIBE_CATEGORY: Category = "aware";
const CONTROL_PANEL_PAGES = new Set(["ControlPanel", "GroupDashboard", "ScreenManagement", "WorkshopManagement", "LabPhotos"]);
const AWAITING_EMAIL_CONFIRMATION_KEY = "tk.awaitingEmailConfirmation";
const LAB_PROGRESS_KEY = "tk.labJourneyProgress";
const LAB_SCORE_REFRESH_EVENT = "tk:labScoreRefresh";
const LAB_FLOW_PAGES = new Set([
  "AwareIntroScreen",
  "VibeQuestions",
  "AwareThreatWallScreen",
  "AwarePillarOthersSaid",
  "AwareCrisisVideoScreen",
  "AwareStayOrLeave",
  "AwareBaselineScoreScreen",
  "PrepareScenarioM1",
  "PrepareScenarioM2",
  "PrepareScenarioM3",
  "PrepareScenarioM4",
  "PrepareScenarioM5",
  "PrepareScenarioM6",
  "PrepareScenarioM7",
  "PrepareScenarioM8",
  "PrepareReflectionScreen",
  "PrepareResultScreen",
  "ShareCompletedScreen",
  "MeetingRoom",
]);

type LabJourneyProgress = {
  aware: boolean;
  prepare: boolean;
  share: boolean;
};

type LabScoreSummary = {
  lab_score: number | null;
  availability_signal: "High" | "Medium" | "Low" | null;
};

const DEFAULT_LAB_PROGRESS: LabJourneyProgress = {
  aware: false,
  prepare: false,
  share: false,
};

function loadStoredLabProgress(): LabJourneyProgress {
  try {
    const parsed = JSON.parse(localStorage.getItem(LAB_PROGRESS_KEY) || "");

    return {
      aware: Boolean(parsed?.aware),
      prepare: Boolean(parsed?.prepare),
      share: Boolean(parsed?.share),
    };
  } catch {
    return DEFAULT_LAB_PROGRESS;
  }
}

function parseVibeCategory(value: string | null | undefined): Category {
  if (value === "aware" || value === "prepare" || value === "share") {
    return value;
  }
  return FALLBACK_VIBE_CATEGORY;
}

export default function App() {
  const [currentPage, setCurrentPage] = React.useState("WelcomeScreen");
  const [role, setRole] = React.useState<CompanyRole>("employee");
  const [roleLoading, setRoleLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [vibeCategory, setVibeCategory] = React.useState<Category>("aware");
  const [prepareModuleCode, setPrepareModuleCode] = React.useState<string | null>(null);
  const [labProgress, setLabProgress] = React.useState<LabJourneyProgress>(loadStoredLabProgress);
  const [labScore, setLabScore] = React.useState<LabScoreSummary | null>(null);

  const markLabProgress = React.useCallback((patch: Partial<LabJourneyProgress>) => {
    setLabProgress((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(LAB_PROGRESS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const refreshLabScore = React.useCallback(async () => {
    if (!isAuthenticated || canUseControlPanel(role)) {
      setLabScore(null);
      return;
    }

    const { data: employeeId } = await supabase.rpc("my_employee_id");

    if (!employeeId) {
      setLabScore(null);
      return;
    }

    await supabase.rpc("recalculate_lab_score", {
      p_employee_id: employeeId,
    });

    const { data } = await supabase
      .from("lab_scores")
      .select("lab_score, availability_signal")
      .eq("employee_id", employeeId)
      .maybeSingle();

    setLabScore(data as LabScoreSummary | null);
  }, [isAuthenticated, role]);

  const handlePageChange = (page: string) => {
    if (page === "JourneyPillars" || page === "MeetingRoom") {
      if (currentPage === "AwareBaselineScoreScreen") {
        markLabProgress({ aware: true });
      }
      if (currentPage === "PrepareResultScreen") {
        markLabProgress({ prepare: true });
      }
      if (currentPage === "PrepareReflectionScreen") {
        markLabProgress({ prepare: true });
      }
      if (currentPage === "ShareInvitation") {
        markLabProgress({ share: true });
      }
      if (currentPage === "VibeQuestions" && vibeCategory === "share") {
        markLabProgress({ share: true });
      }
    }

    // intercept vibe navigation
    if (page.startsWith("VibeQuestions:")) {
      const [, categoryValue, moduleCode] = page.split(":");
      const cat = parseVibeCategory(categoryValue);
      if (cat === "share") {
        sessionStorage.removeItem("tk.sharePhotoResumeIndex");
        sessionStorage.removeItem("tk.sharePhotoDone");
      }
      setVibeCategory(cat);
      setPrepareModuleCode(cat === "prepare" ? moduleCode ?? null : null);

      setCurrentPage("VibeQuestions");
      localStorage.setItem("tk.currentPage", moduleCode ? `VibeQuestions:${cat}:${moduleCode}` : `VibeQuestions:${cat}`);
      return;
    }
    setPrepareModuleCode(null);
    setCurrentPage(page);
    localStorage.setItem("tk.currentPage", page);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  React.useEffect(() => {
    let mounted = true;

    async function restoreSessionAndPage() {
      const { data: { session }} = await supabase.auth.getSession();

      if (!mounted) return;

      const path = window.location.pathname;

      // Allow email confirmation and password reset page without session
      if (!session && (path === "/email-confirmed" || path === "/reset-password")) {
        setIsAuthenticated(false);
        return;
      }

      // If NOT logged in → always go to WelcomeScreen
      if (!session) {
        setIsAuthenticated(false);
        setCurrentPage("WelcomeScreen");
        localStorage.setItem("tk.currentPage", "WelcomeScreen");
        return;
      }

      // If logged in → restore last page or default
      setIsAuthenticated(true);
      const savedPage = localStorage.getItem("tk.currentPage");

      if (savedPage && savedPage !== "Entry") {
        if (savedPage.startsWith("VibeQuestions:")) {
          const [, categoryValue, moduleCode] = savedPage.split(":");
          const cat = parseVibeCategory(categoryValue);
          setVibeCategory(cat);
          setPrepareModuleCode(cat === "prepare" ? moduleCode ?? null : null);
          setCurrentPage("VibeQuestions");
        } else if (savedPage === "TakeAPhoto") {
          setVibeCategory("share");
          setPrepareModuleCode(null);
          setCurrentPage("VibeQuestions");
          localStorage.setItem("tk.currentPage", "VibeQuestions:share");
        } else {
          setCurrentPage(savedPage);
        }
      } else {
        // sensible post-login default
        setCurrentPage("WelcomeScreen");
      }
    }

    restoreSessionAndPage();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    async function loadRole() {
      const { data: { session }} = await supabase.auth.getSession();

      if (!session?.user?.id) {
        setRole("employee");
        setRoleLoading(false);
        return;
      }

      const { data } = await supabase
        .from("employees")
        .select("company_role")
        .eq("auth_user_id", session.user.id)
        .maybeSingle();

      setRole((data?.company_role as CompanyRole) ?? "employee");
      setRoleLoading(false);
    }

    loadRole();
  }, []);

  React.useEffect(() => {
    if (roleLoading || !isAuthenticated) return;

    const savedPage = localStorage.getItem("tk.currentPage");

    const canUseAdminPages = canUseControlPanel(role);

    // Hard guard: only managers/admins may be on control-panel pages.
    if (!canUseAdminPages && CONTROL_PANEL_PAGES.has(currentPage)) {
      setCurrentPage("JourneyPillars");
      localStorage.setItem("tk.currentPage", "JourneyPillars");
      return;
    }

    // If manager/admin and no saved page -> go to ControlPanel
    if (canUseAdminPages && (!savedPage || savedPage === "Entry")) {
      setCurrentPage("ControlPanel");
      return;
    }

    // Employees default to JourneyPillars
    if (!canUseAdminPages && (!savedPage || savedPage === "Entry")) {
      setCurrentPage("JourneyPillars");
    }
  }, [roleLoading, role, isAuthenticated, currentPage]);

  React.useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        const awaitingEmailConfirmation =
          localStorage.getItem(AWAITING_EMAIL_CONFIRMATION_KEY) === "1";
        localStorage.removeItem(AWAITING_EMAIL_CONFIRMATION_KEY);
        localStorage.setItem("tk.currentPage", awaitingEmailConfirmation ? "Entry" : "WelcomeScreen");
        setCurrentPage(awaitingEmailConfirmation ? "Entry" : "WelcomeScreen");
        setIsAuthenticated(false);
        setRole("employee");
        setRoleLoading(false);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    const path = window.location.pathname;

    if (path === "/email-confirmed") {
      setCurrentPage("EmailConfirmed");
      localStorage.removeItem("tk.currentPage");
      return;
    }
  }, []);

  React.useEffect(() => {
    refreshLabScore();
  }, [currentPage, labProgress, refreshLabScore]);

  React.useEffect(() => {
    const refreshOnAnswerChange = () => {
      refreshLabScore();
    };

    window.addEventListener(LAB_SCORE_REFRESH_EVENT, refreshOnAnswerChange);

    return () => {
      window.removeEventListener(LAB_SCORE_REFRESH_EVENT, refreshOnAnswerChange);
    };
  }, [refreshLabScore]);

  React.useEffect(() => {
    if (currentPage !== "JourneyPillars") return;

    const refreshDelays = [500, 1800, 4200, 8000];
    const timers = refreshDelays.map((delay) =>
      window.setTimeout(() => {
        refreshLabScore();
      }, delay)
    );
    const refreshOnFocus = () => refreshLabScore();

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);

    return () => {
      timers.forEach(window.clearTimeout);
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [currentPage, refreshLabScore]);

  React.useEffect(() => {
    const path = window.location.pathname;

    if (path === "/reset-password") {
      setCurrentPage("ResetPassword");
      localStorage.removeItem("tk.currentPage");
      return;
    }
  }, []);

  const pages: { [key: string]: JSX.Element } = {
    // Authentication
    Entry: <Entry handlePageChange={handlePageChange} />,
    AuthCallback: <AuthCallback handlePageChange={handlePageChange} />,
    EmailConfirmed: <EmailConfirmed handlePageChange={handlePageChange} />,
    ResetPassword: <ResetPassword handlePageChange={handlePageChange} />,
    
    // Profile Setup
    ProfileSetup: <UserProfileSetup handlePageChange={handlePageChange} />,
    RolesSetup: <UserRolesSetup handlePageChange={handlePageChange} />,
    Profile: <Profile handlePageChange={handlePageChange} role={role} />,

    // Participant screens
    JourneyPillars: <JourneyPillars handlePageChange={handlePageChange} labProgress={labProgress} labScore={labScore} />,
    AwareIntroScreen: <AwareIntroScreen handlePageChange={handlePageChange} nextPage="VibeQuestions:aware" />,
    VibeQuestions: <VibeQuestionsFlow category={vibeCategory} moduleCode={prepareModuleCode} handlePageChange={handlePageChange} />,
    WorkshopDetails: <WorkshopDetails handlePageChange={handlePageChange} role={role} />,
    MeetingRoom: <MeetingRoom handlePageChange={handlePageChange} />,
    
    // Admin panels
    ControlPanel: <ControlPanel handlePageChange={handlePageChange} />,
    GroupDashboard: <GroupDashboard handlePageChange={handlePageChange} />,
    ScreenManagement: <ScreenManagement handlePageChange={handlePageChange} />,
    WorkshopManagement: <WorkshopManagement handlePageChange={handlePageChange} />,
    LabPhotos: <LabPhotos handlePageChange={handlePageChange} />,

    // Workshop screens
    WelcomeScreen: <WelcomeScreen handlePageChange={handlePageChange} />,
    BlackOutIntro: <BlackOutIntro handlePageChange={handlePageChange} />,
    BlackoutKnowledgeQuestion: <BlackoutKnowledgeQuestion handlePageChange={handlePageChange} />,
    AwarenessIntro: <AwarenessIntro handlePageChange={handlePageChange} />,
    ThreatPerceptionQuestion: <ThreatPerceptionQuestion handlePageChange={handlePageChange} />,
    AwareThreatWallScreen: (
      <AwareThreatWallScreen
        handlePageChange={handlePageChange}
        nextPage="AwarePillarOthersSaid"
        backPage="VibeQuestions:aware"
      />
    ),
    AwarePillarOthersSaid: (
      <OthersSaidV2
        handlePageChange={handlePageChange}
        nextPage="VibeQuestions:aware"
        backPage="AwareThreatWallScreen"
      />
    ),
    AwareCrisisVideoScreen: <AwareCrisisVideoScreen handlePageChange={handlePageChange} nextPage="AwareStayOrLeave" />,
    AwareStayOrLeave: <AwareStayOrLeave handlePageChange={handlePageChange} />,
    AwareBaselineScoreScreen: (
    <AwareBaselineScoreScreen
        handlePageChange={handlePageChange}
        nextPage="JourneyPillars"
      />
    ),
    PrepareResultScreen: (
      <PrepareResultScreen
        handlePageChange={handlePageChange}
        nextPage="JourneyPillars"
      />
    ),
    PrepareReflectionScreen: <PrepareReflectionScreen handlePageChange={handlePageChange} />,

    PrepareScenarioM1: <PrepareScenarioM1 handlePageChange={handlePageChange} />,
    PrepareScenarioM2: <PrepareScenarioM2 handlePageChange={handlePageChange} />,
    PrepareScenarioM3: <PrepareScenarioM3 handlePageChange={handlePageChange} />,
    PrepareScenarioM4: <PrepareScenarioM4 handlePageChange={handlePageChange} />,
    PrepareScenarioM5: <PrepareScenarioM5 handlePageChange={handlePageChange} />,
    PrepareScenarioM6: <PrepareScenarioM6 handlePageChange={handlePageChange} />,
    PrepareScenarioM7: <PrepareScenarioM7 handlePageChange={handlePageChange} />,
    PrepareScenarioM8: <PrepareScenarioM8 handlePageChange={handlePageChange} />,

    OthersSaid: <OthersSaid handlePageChange={handlePageChange} />,
    StayOrLeave: <StayOrLeave handlePageChange={handlePageChange} />,
    StayDecision: <StayDecision handlePageChange={handlePageChange} />,
    PrepareYourself: <PrepareYourself handlePageChange={handlePageChange} />,
    ScenarioPowerOutage: <ScenarioPowerOutage handlePageChange={handlePageChange} />,
    ScenarioBurningBuilding: <ScenarioBurningBuilding handlePageChange={handlePageChange} />,
    ScenarioSecuringDocuments: <ScenarioSecuringDocuments handlePageChange={handlePageChange} />,
    ScenarioEmergency: <ScenarioEmergency handlePageChange={handlePageChange} />,
    ScenarioRecoveryPosition: <ScenarioRecoveryPosition handlePageChange={handlePageChange} />,
    ScenarioCommunicationFailure: <ScenarioCommunicationFailure handlePageChange={handlePageChange} />,
    ScenarioSirene: <ScenarioSirene handlePageChange={handlePageChange} />,
    ScenarioCrisis: <ScenarioCrisis handlePageChange={handlePageChange} />,
    ScenarioOrientation: <ScenarioOrientation handlePageChange={handlePageChange} />,
    ScenarioInformationOverload: <ScenarioInformationOverload handlePageChange={handlePageChange} />,
    ScenarioMentalOverwhelm: <ScenarioMentalOverwhelm handlePageChange={handlePageChange} />,
    ScenarioIsolation: <ScenarioIsolation handlePageChange={handlePageChange} />,
    ScenarioEvacuation: <ScenarioEvacuation handlePageChange={handlePageChange} />,
    EvacuationTaskReview: <EvacuationTaskReview handlePageChange={handlePageChange} />,
    ScenarioPhysicalLimit: <ScenarioPhysicalLimit handlePageChange={handlePageChange} />,
    ShareStateScreen: <ShareStateScreen handlePageChange={handlePageChange} />,
    FamilyPreparedness: <FamilyPreparedness handlePageChange={handlePageChange} />,
    CommunityPreparedness: <CommunityPreparedness handlePageChange={handlePageChange} />,
    AvailabilityAwareness: <AvailabilityAwareness handlePageChange={handlePageChange} />,
    AvailabilityCheck: <AvailabilityCheck handlePageChange={handlePageChange} />,
    EmployeeScore: <EmployeeScreen handlePageChange={handlePageChange} />,
    TakeAPhoto: <TakeAPhoto handlePageChange={handlePageChange} />,
    ShareInvitation: <ShareInvitation handlePageChange={handlePageChange} />,
    ShareCompletedScreen: <ShareCompletedScreen handlePageChange={handlePageChange} />,
    "N/A": <JourneyPillars handlePageChange={handlePageChange} labProgress={labProgress} labScore={labScore} />,

    // Terms and policy
    Terms: <TermsOfService handlePageChange={handlePageChange} role={role} />,
    Privacy: <PrivacyPolicy handlePageChange={handlePageChange} role={role} />,
  };
  const currentElement = pages[currentPage] ?? pages["WelcomeScreen"];
  const showLabHomeButton =
    isAuthenticated &&
    !canUseControlPanel(role) &&
    LAB_FLOW_PAGES.has(currentPage);

  return (
    <ThemeProvider theme={theme}>
      <I18nProvider>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            width: "100%",
            overflowX: "hidden",
            bgcolor: "#F3F4F6",
          }}
        >
          {!canUseControlPanel(role) && (
            <Box
              sx={{
                position: "fixed",
                top: 16,
                right: 16,
                zIndex: 1500,
              }}
            >
              <LanguageSwitcher />
            </Box>
          )}

          {canUseControlPanel(role) && !["Entry","AuthCallback","EmailConfirmed","ResetPassword"].includes(currentPage) && (
            <NavigationBar handlePageChange={handlePageChange} currentPage={currentPage}/>
          )}

          <Box sx={{ flex: 1, minHeight: 0 }}>
            {showLabHomeButton ? (
              <Box sx={{ minHeight: "100%", backgroundColor: "#111010", position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: 12, sm: 18, md: 24 },
                    left: { xs: 12, sm: 18, md: 24 },
                    zIndex: 1200,
                  }}
                >
                  <Button
                    startIcon={<HomeOutlinedIcon />}
                    onClick={() => handlePageChange("JourneyPillars")}
                    sx={{
                      borderRadius: 999,
                      px: { xs: 1.4, sm: 1.8 },
                      minHeight: { xs: 34, sm: 38 },
                      color: "#fff",
                      bgcolor: "rgba(20,20,20,0.58)",
                      border: "1px solid rgba(255,255,255,0.20)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
                      textTransform: "none",
                      fontWeight: 900,
                      "&:hover": {
                        bgcolor: "rgba(35,35,35,0.72)",
                      },
                    }}
                  >
                    Home
                  </Button>
                </Box>
                {currentElement}
              </Box>
            ) : (
              currentElement
            )}
          </Box>
          
          <Footer handlePageChange={handlePageChange}/>
        </Box>
      </I18nProvider>
    </ThemeProvider>
  );
}
